import { RootState } from '@/app/store';
import { apiConfig } from '@/config';
import { Middleware, MiddlewareAPI, isRejectedWithValue } from '@reduxjs/toolkit';
import { BaseQueryFn, FetchArgs, FetchBaseQueryError, createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { Mutex } from 'async-mutex';
import { PostAuthRefreshApiResponse } from '.';

const mutex = new Mutex();

const baseQuery = fetchBaseQuery({
  baseUrl: apiConfig.baseUrl,
  prepareHeaders: (headers, { getState }) => {
    const {
      auth: { accessToken, currentTenant },
    } = getState() as RootState;
    if (accessToken) {
      headers.set('Authorization', `Bearer ${accessToken}`);
    }

    // Add tenant header if available in localStorage
    const currentTenantIdFromLocalStorage = localStorage.getItem('currentTenantId');
    const currentTenantId = currentTenantIdFromLocalStorage || currentTenant?.id;
    if (currentTenantId) {
      headers.set('X-Tenant-Id', currentTenantId);
    }

    return headers;
  },
});

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  // wait until the mutex is available without locking it
  // await mutex.waitForUnlock();
  let result = await baseQuery(args, api, extraOptions);

  if (result.error && (result.error.status === 401 || result.error.status === 403)) {
    // checking whether the mutex is locked
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();
      try {
        const refreshToken = (api.getState() as RootState).auth.refreshToken;
        const refreshResult = await baseQuery(
          {
            url: '/auth/refresh',
            method: 'POST',
            body: {
              refreshToken,
            },
          },
          api,
          extraOptions,
        );
        if (refreshResult.data) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const data = refreshResult.data as PostAuthRefreshApiResponse;
          api.dispatch({
            type: 'auth/tokenUpdated',
            payload: {
              accessToken: data.data.accessToken,
              refreshToken: data.data.refreshToken,
            },
          });
          // retry the initial query
          result = await baseQuery(args, api, extraOptions);
        } else {
          api.dispatch({ type: 'auth/logout' });
        }
      } finally {
        // release must be called once the mutex should be released again.
        release();
      }
    } else {
      // wait until the mutex is available without locking it
      await mutex.waitForUnlock();
      result = await baseQuery(args, api, extraOptions);
    }
  }
  return result;
};

/**
 * Log a warning and show a toast!
 */
export const rtkQueryErrorLogger: Middleware =
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  (_api: MiddlewareAPI) => (next) => (action) => {
    // RTK Query uses `createAsyncThunk` from redux-toolkit under the hood, so we're able to utilize these matchers!
    if (isRejectedWithValue(action)) {
      console.log(JSON.stringify(action.payload));
    }

    return next(action);
  };

/**
 * Defines the empty configuration api, will allow for code splitting the middleware across multiple files
 *
 * @resources
 * Code splitting: https://redux-toolkit.js.org/rtk-query/usage/code-splitting
 */
export const ConfigApi = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  endpoints: () => ({}),
  tagTypes: ['user', 'auth', 'location', 'device', 'alerts', 'alert_rule', 'tenants', 'tenant_members'],
});
